import {
    sub as subDuration,
    Duration,
    differenceInHours,
    differenceInCalendarDays,
    differenceInCalendarWeeks,
    differenceInCalendarMonths,
    eachHourOfInterval,
    eachDayOfInterval,
    eachWeekOfInterval,
    eachMonthOfInterval,
    startOfHour,
    startOfDay,
    startOfWeek,
    startOfMonth,
    endOfHour,
    endOfDay,
    endOfWeek,
    endOfMonth,
    endOfYear,
    startOfYear,
    eachYearOfInterval,
    differenceInYears,
} from 'date-fns';
import { Interval, ViewMode } from 'app/agriculture/AgTimeline/types';
import { TZDate } from '@date-fns/tz';

export type BucketSize = 'hour' | 'day' | 'week' | 'month' | 'year';

export const bucketConfig: Record<
    BucketSize,
    {
        bucketDuration: Duration;
        startOfBucket: (date: Date) => Date;
        endOfBucket: (date: Date) => Date;
        eachBucketOfInterval: (interval: Interval<Date>) => Date[];
        differenceInBuckets: (date1: Date, date2: Date) => number;
        viewModesThatFetchAllBuckets: (keyof typeof ViewMode)[];
    }
> = {
    hour: {
        bucketDuration: { hours: 1 },
        startOfBucket: startOfHour,
        endOfBucket: endOfHour,
        eachBucketOfInterval: eachHourOfInterval,
        differenceInBuckets: differenceInHours,
        viewModesThatFetchAllBuckets: ['Day'],
    },
    day: {
        bucketDuration: { days: 1 },
        startOfBucket: startOfDay,
        endOfBucket: endOfDay,
        eachBucketOfInterval: eachDayOfInterval,
        differenceInBuckets: differenceInCalendarDays,
        viewModesThatFetchAllBuckets: ['Day', 'Week', 'Month'],
    },
    week: {
        bucketDuration: { weeks: 1 },
        startOfBucket: (date) => startOfWeek(date, { weekStartsOn: 1 }),
        endOfBucket: (date) => endOfWeek(date, { weekStartsOn: 1 }),
        eachBucketOfInterval: (interval) => eachWeekOfInterval(interval, { weekStartsOn: 1 }),
        differenceInBuckets: (date1, date2) =>
            differenceInCalendarWeeks(date1, date2, { weekStartsOn: 1 }),
        viewModesThatFetchAllBuckets: ['Day', 'Week', 'Month', 'SixMonth'],
    },
    month: {
        bucketDuration: { months: 1 },
        startOfBucket: startOfMonth,
        endOfBucket: endOfMonth,
        eachBucketOfInterval: eachMonthOfInterval,
        differenceInBuckets: differenceInCalendarMonths,
        viewModesThatFetchAllBuckets: ['Day', 'Week', 'Month', 'SixMonth'],
    },
    year: {
        bucketDuration: { years: 1 },
        startOfBucket: startOfYear,
        endOfBucket: endOfYear,
        eachBucketOfInterval: eachYearOfInterval,
        differenceInBuckets: differenceInYears,
        viewModesThatFetchAllBuckets: ['Day', 'Week', 'Month', 'SixMonth'],
    },
} as const;

// this order matters for prefetching snapshot buckets
export const allBucketSizes = [
    'year',
    'month',
    'week',
    'day',
    'hour',
] as const satisfies (keyof typeof bucketConfig)[];

export function getBucketInterval(
    date: Date = new Date(),
    { bucketSize, timeZone }: { bucketSize: BucketSize; timeZone?: string },
) {
    const { endOfBucket, bucketDuration } = bucketConfig[bucketSize];
    const end = endOfBucket(new TZDate(date, timeZone));
    const start = subDuration(end, bucketDuration);

    return { start, end };
}
